'use client';

import { cn } from '@/lib/utils';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import type { Locale } from '@/features/marketing/hooks/useLocale';
import { PAPER_INK, PAPER_FOCUS, PAPER_VARIANT } from '@/features/marketing/ui/paper-tokens';

const LOCALES: { code: Locale; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'ro', label: 'RO' },
];

export type LanguageToggleProps = {
  /**
   * 2026-07-17 R7 integration seam: this control renders inside
   * `LandingNavbar`'s single instance, which itself gained an additive
   * `surface?: 'paper' | 'dark'` prop (default `'paper'`) so the 3
   * pre-R7 dark public pages (`ChangelogPage`/`BlogPostLayout`/
   * `BlogIndexPage`) keep rendering pixel-identical to before R7. This
   * toggle has no page context of its own (it is a leaf, not a page), so
   * it inherits the SAME surface value from its one caller rather than
   * re-deriving it — "accept a surface prop" per the integration brief's
   * smallest-correct-shape instruction. Default `'paper'` matches the
   * navbar's own default; the dark branch reproduces this control's
   * pre-R7 recipe verbatim (`text-gray-400 hover:text-gray-200`,
   * unchanged — this file itself never changed pre-R7, the navbar always
   * composed it plain).
   */
  surface?: 'paper' | 'dark';
};

export default function LanguageToggle({ surface = 'paper' }: LanguageToggleProps) {
  const { locale, setLocale } = useLocale();
  const paper = surface === 'paper';

  return (
    <div className="flex items-center gap-0.5">
      {LOCALES.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => setLocale(code)}
          className={cn(
            'px-2 py-1 text-xs font-semibold tracking-wider transition-colors rounded',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            paper ? PAPER_FOCUS.ring : 'focus-visible:ring-[#FBBF24]',
            paper && PAPER_VARIANT.baseRingOffset,
            locale === code
              ? 'text-amber-500 underline underline-offset-2'
              : paper
                ? cn(PAPER_INK.muted.text, PAPER_VARIANT.primaryHoverText)
                : 'text-gray-400 hover:text-gray-200'
          )}
          aria-pressed={locale === code}
          aria-label={`Switch to ${label}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
